import { useState } from "react";
import { IconButton, Modal, Tooltip } from "@mui/material";
import { Sync } from "@mui/icons-material";
import IframeViewer from "./IframeViewer";

const styles = {
  // Flex-centred, not absolute+transform: IframeViewer's maximized state
  // relies on position:fixed reaching the true viewport, and a transformed
  // ancestor would trap that to its own box instead.
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  panel: {
    backgroundColor: "#fff",
    borderRadius: "6px",
    boxShadow: "0 16px 48px rgba(16,24,40,.28)",
    overflow: "hidden",
  },
};

// 75% of the viewport, taken once at mount — matching how the resize hooks
// themselves only read window dimensions at their own initial state, not on
// every resize.
const DEFAULT_SIZE_RATIO = 0.75;

export const ReportModal = ({ modal, setModal }) => {
  const [maximized, setMaximized] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [defaultHeight] = useState(() =>
    Math.round(window.innerHeight * DEFAULT_SIZE_RATIO)
  );
  const [defaultWidth] = useState(() =>
    Math.round(window.innerWidth * DEFAULT_SIZE_RATIO)
  );

  const handleClose = () => {
    setModal({ isOpen: false });
    setMaximized(false);
  };

  // Remounting the iframe (new key) forces a fresh navigation to the same
  // URL. Independent of the maximize toggle, which must never itself force
  // a reload.
  const handleReload = () => setReloadKey((key) => key + 1);

  return (
    <Modal
      open={modal?.isOpen}
      onClose={handleClose}
      sx={styles.modal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <IframeViewer
        title="report"
        src={modal?.url}
        iframeKey={reloadKey}
        sx={styles.panel}
        toolbarStart={
          <Tooltip title="Reload the report">
            <IconButton size="small" onClick={handleReload}>
              <Sync fontSize="small" />
            </IconButton>
          </Tooltip>
        }
        resizable
        resizeStorageKey="report-modal"
        defaultHeight={defaultHeight}
        minHeight={300}
        maxHeight={1200}
        resizableWidth
        widthResizeStorageKey="report-modal"
        defaultWidth={defaultWidth}
        minWidth={500}
        maxWidth={1600}
        maximized={maximized}
        onMaximizedChange={setMaximized}
        closable
        onClose={handleClose}
      />
    </Modal>
  );
};

export const useReportModal = () => {
  const [modal, setModal] = useState({ isOpen: false });

  return {
    reportModal: <ReportModal modal={modal} setModal={setModal} />,
    setReportModalOpen: (url) => {
      setModal({ isOpen: true, url });
    },
  };
};
